import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './PrayerMeetings.css';
import API_BASE_URL from '../config/api';

const localizer = momentLocalizer(moment);

const PRAYER_GROUPS = [
  'Munnekollala',
  'Marathahalli', 
  'Ramesh Nagar',
  'AECS',
  'Sarjapura',
  'Whitefield',
  'Murgeshpalya'
];

const PrayerMeetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [families, setFamilies] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredFamilies, setFilteredFamilies] = useState([]);

  const [formData, setFormData] = useState({
    hofName: '',
    date: moment().format('YYYY-MM-DD'),
    time: '18:30',
    prayerGroup: PRAYER_GROUPS[0],
    address: '',
    mapsLocation: '',
    contactNumber: ''
  });

  useEffect(() => {
    fetchMeetings();
    fetchFamilies();
  }, []);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/prayer-meetings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMeetings(data);
      } else {
        setError('Failed to fetch meetings');
      }
    } catch (error) {
      setError('Error loading meetings');
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/families`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFamilies(data);
      } else {
        console.error('Failed to fetch families, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleMonthNavigate = (direction) => {
    if (direction === 'prev') {
      setCurrentMonth(prev => moment(prev).subtract(1, 'month'));
    } else if (direction === 'next') {
      setCurrentMonth(prev => moment(prev).add(1, 'month'));
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'hofName') {
      if (value.length > 0) {
        const filtered = families.filter(family => {
          // Find Head of Family from members array
          const hofMember = family.members?.find(member => 
            member.relationshipToHof === "Head of Family"
          );
          const hofName = hofMember?.fullName;
          
          return hofName && hofName.toLowerCase().includes(value.toLowerCase());
        });
        setFilteredFamilies(filtered);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    }
  };

  const handleFamilySelect = (family) => {
    // Find Head of Family from members array
    const hofMember = family.members?.find(member => 
      member.relationshipToHof === "Head of Family"
    );
    const hofName = hofMember?.fullName || '';
    
    setFormData(prev => ({
      ...prev,
      hofName: hofName,
      address: family.contactAddress || ''
    }));
    setShowSuggestions(false);
  };

  const handleAddMeeting = () => {
    setShowAddForm(true);
    setEditingMeeting(null);
    setFormData({
      hofName: '',
      date: moment(selectedDate).format('YYYY-MM-DD'),
      time: '18:30',
      prayerGroup: PRAYER_GROUPS[0],
      address: '',
      mapsLocation: '',
      contactNumber: ''
    });
  };

  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setShowAddForm(true);
    setFormData({
      hofName: meeting.hofName || '',
      date: moment(meeting.date).format('YYYY-MM-DD'),
      time: meeting.time || '18:30',
      prayerGroup: meeting.prayerGroup || PRAYER_GROUPS[0],
      address: meeting.address || '',
      mapsLocation: meeting.mapsLocation || '',
      contactNumber: meeting.contactNumber || ''
    });
  };

  const handleSaveMeeting = async () => {
    try {
      const url = editingMeeting 
        ? `${API_BASE_URL}/prayer-meetings/${editingMeeting.id}`
        : `${API_BASE_URL}/prayer-meetings`;
      
      const method = editingMeeting ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        await fetchMeetings();
        setShowAddForm(false);
        setEditingMeeting(null);
      } else {
        setError('Failed to save meeting');
      }
    } catch (error) {
      setError('Error saving meeting');
      console.error('Error saving meeting:', error);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (window.confirm('Are you sure you want to delete this meeting?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/prayer-meetings/${meetingId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        
        if (response.ok) {
          await fetchMeetings();
        } else {
          setError('Failed to delete meeting');
        }
      } catch (error) {
        setError('Error deleting meeting');
        console.error('Error deleting meeting:', error);
      }
    }
  };

  const getMeetingsForDate = (date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    return meetings.filter(meeting => 
      moment(meeting.date).format('YYYY-MM-DD') === dateStr
    );
  };

  const getMeetingsForMonth = () => {
    return meetings.filter(meeting => 
      moment(meeting.date).isSame(currentMonth, 'month')
    ).sort((a, b) => moment(a.date).diff(moment(b.date)));
  };

  const generateCalendarEvents = () => {
    return meetings.map(meeting => ({
      id: meeting.id,
      title: `${meeting.prayerGroup} Prayer`,
      start: moment(`${meeting.date} ${meeting.time}`).toDate(),
      end: moment(`${meeting.date} ${meeting.time}`).add(1, 'hour').toDate(),
      resource: meeting
    }));
  };

  const dayPropGetter = (date) => {
    const isSelected = moment(date).isSame(selectedDate, 'day');
    const meetingsCount = getMeetingsForDate(date).length;
    
    return {
      className: `calendar-day ${isSelected ? 'selected' : ''} ${meetingsCount > 0 ? 'has-meetings' : ''}`,
      style: {
        backgroundColor: isSelected ? '#e3f2fd' : undefined
      }
    };
  };

  return (
    <div className="prayer-meetings-container">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      <div className="top-section">
        <div className="calendar-section">
          <div className="calendar-header">
            <button 
              className="nav-btn" 
              onClick={() => handleMonthNavigate('prev')}
            >
              ◀
            </button>
            <h2>{currentMonth.format('MMMM YYYY')}</h2>
            <button 
              className="nav-btn" 
              onClick={() => handleMonthNavigate('next')}
            >
              ▶
            </button>
          </div>
          
          <div className="calendar-wrapper">
            <Calendar
              localizer={localizer}
              events={generateCalendarEvents()}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 400 }}
              view="month"
              onView={() => {}}
              toolbar={false}
              date={currentMonth.toDate()}
              onNavigate={() => {}}
              onSelectSlot={({ start }) => handleDateSelect(start)}
              onSelectEvent={(event) => handleDateSelect(event.start)}
              selectable
              dayPropGetter={dayPropGetter}
              eventPropGetter={() => ({
                style: {
                  backgroundColor: '#4CAF50',
                  borderRadius: '3px',
                  opacity: 0.8,
                  color: 'white',
                  border: '0px',
                  display: 'block'
                }
              })}
            />
          </div>
        </div>

        <div className="monthly-list-section">
          <h3>Meetings for {currentMonth.format('MMMM YYYY')}</h3>
          <div className="monthly-list">
            {loading ? (
              <div className="loading">Loading meetings...</div>
            ) : (
              getMeetingsForMonth().map(meeting => (
                <div 
                  key={meeting.id} 
                  className="monthly-meeting-item"
                  onClick={() => handleDateSelect(new Date(meeting.date))}
                >
                  <div className="date-badge">
                    {moment(meeting.date).format('MMM DD')}
                  </div>
                  <div className="meeting-details">
                    <div className="meeting-title">{meeting.prayerGroup} Prayer</div>
                    <div className="meeting-info">
                      {meeting.time} • {meeting.hofName}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="bottom-section">
        <div className="day-header">
          <h3>{moment(selectedDate).format('dddd, MMMM DD, YYYY')}</h3>
        </div>

        <div className="day-meetings">
          {getMeetingsForDate(selectedDate).map(meeting => (
            <div key={meeting.id} className="meeting-card">
              <div className="meeting-time">{meeting.time}</div>
              <div className="meeting-prayer-group">
                <span className="prayer-group-label">{meeting.prayerGroup}</span>
              </div>
              <div className="meeting-hof-name">{meeting.hofName}</div>
              <div className="meeting-actions">
                <button 
                  className="edit-btn"
                  onClick={() => handleEditMeeting(meeting)}
                  title="Edit"
                >
                  ✏️
                </button>
                <button 
                  className="delete-btn"
                  onClick={() => handleDeleteMeeting(meeting.id)}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="add-meeting-section">
          {!showAddForm ? (
            <button className="add-meeting-btn" onClick={handleAddMeeting}>
              + Add Prayer Meeting
            </button>
          ) : (
            <div className="add-meeting-form">
              <h4>{editingMeeting ? 'Edit Prayer Meeting' : 'Add Prayer Meeting'}</h4>
              
              <div className="form-row">
                <label>
                  Time:
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleFormChange('time', e.target.value)}
                  />
                </label>
                
                <label>
                  Date:
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleFormChange('date', e.target.value)}
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  HOF Name:
                  <div className="autosuggest-container">
                    <input
                      type="text"
                      value={formData.hofName}
                      onChange={(e) => handleFormChange('hofName', e.target.value)}
                      onFocus={() => {
                        if (formData.hofName && filteredFamilies.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowSuggestions(false), 200);
                      }}
                      placeholder="Head of Family name"
                    />
                    {showSuggestions && (
                      <div className="suggestions-dropdown">
                        {filteredFamilies.length > 0 ? (
                          filteredFamilies.map((family, index) => (
                            <div
                              key={index}
                              className="suggestion-item"
                              onClick={() => handleFamilySelect(family)}
                            >
                              <div className="suggestion-name">
                                {family.members?.find(member => 
                                  member.relationshipToHof === "Head of Family"
                                )?.fullName || family.familyName}
                              </div>
                              {family.contactAddress && (
                                <div className="suggestion-address">{family.contactAddress}</div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="suggestion-item no-results">
                            No families found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </label>
                
                <label>
                  Prayer Group:
                  <select
                    value={formData.prayerGroup}
                    onChange={(e) => handleFormChange('prayerGroup', e.target.value)}
                  >
                    {PRAYER_GROUPS.map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="form-row">
                <label>
                  Address:
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleFormChange('address', e.target.value)}
                    placeholder="Full address"
                  />
                </label>
                
                <label>
                  Contact Number:
                  <input
                    type="tel"
                    value={formData.contactNumber}
                    onChange={(e) => handleFormChange('contactNumber', e.target.value)}
                    placeholder="Phone number"
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  Maps Location:
                  <input
                    type="url"
                    value={formData.mapsLocation}
                    onChange={(e) => handleFormChange('mapsLocation', e.target.value)}
                    placeholder="Google Maps URL"
                  />
                </label>
              </div>

              <div className="form-actions">
                <button 
                  className="cancel-btn" 
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </button>
                <button 
                  className="save-btn" 
                  onClick={handleSaveMeeting}
                >
                  {editingMeeting ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrayerMeetings;